import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Box, Button } from "@metrea-ui/v2";
import { LoadingIndicator } from "src/components/UI/common/LoadIndicator";
import { PageHeader } from "../SiteLayout/Header";
import { PageFooter } from "../SiteLayout/Footer";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useLogsData } from "src/hooks/useLogsData";
import { errorLog, debugLog } from "src/utils/debugUtils";

import UserActivitiesList from "./UserActivitiesList";

const defaultFilters = {
	limit: 100,
	offset: 0,
	sortBy: "log_dt",
	sortOrder: "desc",
	// Default to the pre-T-01033 behavior until the user explicitly narrows the login bucket.
	loginFilter: "both",
};

const UserActivities = () => {
	const { withError } = useDialogBox();
	// User Activity intentionally uses the POST search path instead of the generic GET log reader.
	const { searchLogs } = useLogsData();
	const items = useSelector((state) => state.logs.items);
	const meta = useSelector((state) => state.logs.meta);
	const fetchStatus = useSelector((state) => state.logs.status.search);
	const fetchError = useSelector((state) => state.logs.error.search);
	const [activeFilters, setActiveFilters] = useState(defaultFilters);

	const loadActivity = useCallback((filters = defaultFilters) => {
		// Centralize the User Activity POST-search path so refresh, retry, paging,
		// sort, and filter actions all stay on the same request model.
		const nextFilters = { ...defaultFilters, ...filters };
		debugLog(2, "[UserActivities][FLOW] loadActivity started", { filters: nextFilters });
		setActiveFilters(nextFilters);
		return searchLogs(nextFilters);
	}, [searchLogs]);

	const handleApplyFilters = useCallback((filters) => {
		// Preserve the current server sort/page-size preferences when the user changes only filter inputs.
		return loadActivity({ ...activeFilters, ...filters, offset: 0 });
	}, [activeFilters, loadActivity]);

	const handleClearFilters = useCallback(() => {
		// Clear only the filter fields; keep the current sort and rows-per-page selection on this page.
		return loadActivity({
			...activeFilters,
			keyword: "",
			from: "",
			to: "",
			page: "",
			actingAs: "",
			event: "",
			ip: "",
			loginFilter: "both",
			offset: 0,
		});
	}, [activeFilters, loadActivity]);

	const handleBatchPageChange = useCallback((direction) => {
		const limit = Number(activeFilters.limit) || defaultFilters.limit;
		const currentOffset = Number(activeFilters.offset) || 0;
		const nextOffset = direction === "next" ? currentOffset + limit : Math.max(currentOffset - limit, 0);
		return loadActivity({ ...activeFilters, offset: nextOffset });
	}, [activeFilters, loadActivity]);

	const handlePageSizeChange = useCallback((limit) => {
		return loadActivity({ ...activeFilters, limit, offset: 0 });
	}, [activeFilters, loadActivity]);

	const handleSortChange = useCallback(({ sortBy, sortOrder }) => {
		return loadActivity({ ...activeFilters, sortBy, sortOrder, offset: 0 });
	}, [activeFilters, loadActivity]);

	useEffect(() => {
		loadActivity();
	}, [loadActivity]);

	useEffect(() => {
		if (!fetchError) return;

		errorLog(2, "[UserActivities][FLOW] loadActivity failed", { error: fetchError });
		withError(fetchError, () => loadActivity(activeFilters), {
			fallbackMessage: "Retrieve User Activity Error",
		});
	}, [activeFilters, fetchError, loadActivity, withError]);

	return (
		<>
			<PageHeader pageTitle="User Activity" actions={true}>
				<Button onClick={() => loadActivity(activeFilters)} disabled={fetchStatus === "loading"}>
					Refresh
				</Button>
			</PageHeader>

			<Box className="pageContent">
				<LoadingIndicator open={fetchStatus === "loading"} />

				<UserActivitiesList
					items={items}
					meta={meta}
					loading={fetchStatus === "loading"}
					onApplyFilters={handleApplyFilters}
					onClearFilters={handleClearFilters}
					onNextBatch={() => handleBatchPageChange("next")}
					onPrevBatch={() => handleBatchPageChange("prev")}
					onPageSizeChange={handlePageSizeChange}
					onSortChange={handleSortChange}
				/>

				<PageFooter />
			</Box>
		</>
	);
};

export default UserActivities;
